import Immutable from 'immutable';
import type { Map } from 'immutable';

import {
  ADD_SETTING_LINE,
  DELETE_SETTING_LINE,
  GET_PRODUCT_TYPES,
  GET_SETTING_LINE,
  SAVE_SETTING_LINE,
} from './actions';
import { productConditionMap } from '../../components/condition-line';
import type {
  ProductButtonAction,
  ProductName,
  ProductSettingPayload,
} from './product-settings.types';

interface ProductCondition {
  category: string;
  [field: string]: unknown;
}

interface ProductResponseData extends Record<string, unknown> {
  conditions?: ProductCondition[];
  items?: Array<Record<string, unknown> & { id: number }>;
}

interface ProductReducerAction {
  buttonAction?: ProductButtonAction;
  data?: { data: ProductResponseData };
  errorMessage?: string;
  itemIndex?: number;
  newData?: ProductSettingPayload;
  product?: string;
  settingLineId?: number;
  shopId?: string;
  type: string;
  typeIndex?: number;
  values?: ProductSettingPayload;
}

const filterOutRemovedConditions = (
  conditions: ProductCondition[],
  product: ProductName,
) =>
  conditions.filter((conditionLine) =>
    productConditionMap[product].includes(
      conditionLine.category as (typeof productConditionMap)[ProductName][number],
    ),
  );

function productReducer(
  state: Map<unknown, unknown> = Immutable.Map(),
  action: ProductReducerAction,
) {
  const shopId = action.shopId as string;
  const product = action.product as ProductName;
  const typeIndex = action.typeIndex as number;
  const settingLineId = action.settingLineId as number;

  switch (action.type) {
    case GET_PRODUCT_TYPES + '_LOADING': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex], Immutable.fromJS({loading: true, error: ''}));
    } case GET_PRODUCT_TYPES + '_ERROR': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex], Immutable.fromJS({loading: false, error: action.errorMessage}));
    } case GET_PRODUCT_TYPES: {
      //sort items by ID
      const responseData = action.data!.data;
      const itemsById = (responseData.items ?? []).reduce(
        (lookup, item) => lookup.set(item.id, Immutable.fromJS(item)),
        Immutable.Map<number, Map<string, unknown>>(),
      );
      const dataWithoutItems = Object.fromEntries(
        Object.entries(responseData).filter(([field]) => field !== 'items'),
      );
      action.data!.data = dataWithoutItems;
      const data = {items: itemsById, ...dataWithoutItems};

      const newState = state.setIn([shopId, product, 'data', typeIndex], Immutable.fromJS(data));
      return newState.setIn([shopId, product, 'UIStates', typeIndex], Immutable.fromJS({loading: false, error: ''}));
    }
    case ADD_SETTING_LINE: {
      const itemIndex = action.itemIndex as number;
      const newState = state.setIn([shopId, product, 'data', typeIndex, 'items', itemIndex], Immutable.fromJS(action.newData));
      return newState.mergeIn([shopId, product, 'UIStates', typeIndex, itemIndex], Immutable.fromJS({loading: false, adding: false, error: ''}));
    }
    case GET_SETTING_LINE + '_LOADING': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({loading: true, error: ''}));
    } case GET_SETTING_LINE + '_ERROR': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({loading: false, error: action.errorMessage}));
    } case GET_SETTING_LINE: {
      const data = action.data!.data;
      data.conditions = filterOutRemovedConditions(data.conditions ?? [], product);
      const newState = state.setIn([shopId, product, 'data', typeIndex, 'items', settingLineId], Immutable.fromJS(data));
      return newState.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({loading: false, error: ''}));
    }
    case SAVE_SETTING_LINE + '_LOADING': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({[action.buttonAction as ProductButtonAction]: true, error: ''}));
    } case SAVE_SETTING_LINE + '_ERROR': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({[action.buttonAction as ProductButtonAction]: false, error: action.errorMessage}));
    } case SAVE_SETTING_LINE: {
      const newState = state.mergeIn([shopId, product, 'data', typeIndex, 'items', settingLineId], Immutable.fromJS(action.values));
      return newState.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({[action.buttonAction as ProductButtonAction]: false, error: ''}));
    }
    case DELETE_SETTING_LINE + '_LOADING': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({deleting: true, error: ''}));
    } case DELETE_SETTING_LINE + '_ERROR': {
      return state.mergeIn([shopId, product, 'UIStates', typeIndex, settingLineId], Immutable.fromJS({deleting: false, error: action.errorMessage}));
    } case DELETE_SETTING_LINE: {
      const newState = state.deleteIn([shopId, product, 'data', typeIndex, 'items', settingLineId]);
      return newState.deleteIn([shopId, product, 'UIStates', typeIndex, settingLineId]);
    }
    default: {
      return state;
    }
  }
}

export default productReducer;
